import type { NodeViewRendererProps } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { NodeConfig } from '@tiptap/react';
import {
  markInputRule,
  markPasteRule,
  mergeAttributes,
  Node,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  textblockTypeInputRule,
} from '@tiptap/react';
import { pipe } from 'fp-ts/function';
import type { ComponentType } from 'react';
import React from 'react';
import type {
  ExtensionProps,
  HtmlRicosNodeExtension,
  ReactRicosNodeExtension,
  RicosExtension,
  RicosNodeExtension,
  RicosServices,
} from 'ricos-types';
import { isRicosNodeExtension } from 'ricos-types';

import { RicosNode } from '../components/RicosNode';
import type {
  DecoratedNodeExtension,
  ExtensionAggregate,
  IHtmlNodeExtension,
  IReactNodeExtension,
} from './domain-types';
import { DEFAULT_PRIORITY } from './domain-types';

const toExtensionConfig = (ext: RicosNodeExtension) => {
  if (ext.type === 'html-node') {
    return ext.createExtensionConfig({
      textblockTypeInputRule,
      markPasteRule,
      markInputRule,
      mergeAttributes,
      Plugin,
      PluginKey,
    });
  }

  return ext.createExtensionConfig({
    textblockTypeInputRule,
    markPasteRule,
    markInputRule,
    mergeAttributes,
    Plugin,
    PluginKey,
  });
};

// @TODO: REWRITE THIS. If this was actually included in each respective node and handled by itself
// then we would have a normal text editor that you copy content from and in!!!
// This is horrible because content copied from us cannot be used in other editors!
const toFullNodeConfig =
  (ext: RicosNodeExtension) =>
  (config: NodeConfig): NodeConfig => {
    // omit addKeyboardShortcuts
    const { addKeyboardShortcuts, ...rest } = config;
    return {
      ...(ext.groups.includes('text')
        ? {}
        : {
            parseHTML: () => [
              {
                tag: `${config.name}-component`,
                getAttrs: node => {
                  const attrs = (node as any).getAttribute?.('data-node-attrs');
                  return JSON.parse(attrs);
                },
              },
            ],
            renderHTML: ({ HTMLAttributes }) => {
              return [
                `${config.name}-component`,
                {
                  'data-node-attrs': JSON.stringify(HTMLAttributes),
                },
                ...(ext.groups.includes('node-container') ? [0] : []),
              ];
            },
          }),
      ...rest,
      ...(ext.groups.includes('shortcuts-enabled') ? { addKeyboardShortcuts } : {}),
    };
  };

const createRicosNodeConfig = (ext: RicosNodeExtension): NodeConfig =>
  pipe(ext, toExtensionConfig, toFullNodeConfig(ext));

const createRicosNodeHOC =
  (settings: Record<string, unknown>) =>
  (Component: ComponentType) =>
  (props: NodeViewRendererProps) =>
    (
      <NodeViewWrapper as="div">
        <RicosNode Component={Component} tiptapNodeProps={{ ...props, settings }} />
      </NodeViewWrapper>
    );

export class ReactNodeExtension implements IReactNodeExtension {
  config: NodeConfig;

  priority: number;

  type = 'react-node' as const;

  name: string;

  groups: ReactRicosNodeExtension['groups'];

  protected readonly settings: Record<string, unknown>;

  private readonly ricosExtension: ReactRicosNodeExtension;

  protected readonly reconfigure: (
    config: NodeConfig,
    extensions: RicosExtension[],
    ricosProps: ExtensionProps,
    settings: Record<string, unknown>,
    services: RicosServices
  ) => NodeConfig;

  constructor(extension: ReactRicosNodeExtension, config?: NodeConfig) {
    if (!isRicosNodeExtension(extension)) {
      throw new TypeError('invalid argument');
    }
    this.ricosExtension = extension;
    this.config = config || createRicosNodeConfig(extension);
    this.priority = this.config.priority || DEFAULT_PRIORITY;
    this.name = this.config.name;
    this.groups = extension.groups;
    this.settings = extension.settings || {};
    this.reconfigure = extension.reconfigure || (() => this.config);
  }

  getComponent() {
    return this.ricosExtension.Component as ComponentType;
  }

  asRenderable(decoratedComponent: ComponentType) {
    return new RenderableNodeExtension(decoratedComponent, this.ricosExtension);
  }

  getRicosExtension() {
    return this.ricosExtension;
  }
}

class RenderableNodeExtension extends ReactNodeExtension implements DecoratedNodeExtension {
  constructor(component: ComponentType, extension: ReactRicosNodeExtension) {
    super(extension);
    this.config = {
      addNodeView: () => pipe(component, createRicosNodeHOC(this.settings), ReactNodeViewRenderer),
      ...this.config,
    };
  }

  toTiptapExtension(
    extensions: ExtensionAggregate,
    ricosProps: ExtensionProps,
    services: RicosServices
  ) {
    const ricosExtensions = extensions.getRicosExtensions();
    const config = this.reconfigure(
      this.config,
      ricosExtensions,
      ricosProps,
      this.settings,
      services
    );
    return Node.create(config);
  }
}

export class HtmlNodeExtension implements IHtmlNodeExtension {
  config: NodeConfig;

  priority: number;

  type = 'html-node' as const;

  name: string;

  private readonly ricosExtension: HtmlRicosNodeExtension;

  private readonly settings: Record<string, unknown>;

  private readonly reconfigure: (
    config: NodeConfig,
    extensions: RicosExtension[],
    ricosProps: ExtensionProps,
    settings: Record<string, unknown>,
    services: RicosServices
  ) => NodeConfig;

  groups: HtmlRicosNodeExtension['groups'];

  constructor(extension: HtmlRicosNodeExtension, config?: NodeConfig) {
    if (!isRicosNodeExtension(extension)) {
      throw new TypeError('invalid argument');
    }
    this.ricosExtension = extension;
    this.config = config || createRicosNodeConfig(extension);
    this.priority = this.config.priority || DEFAULT_PRIORITY;
    this.name = this.config.name;
    this.groups = extension.groups || [];
    this.settings = extension.settings || {};
    this.reconfigure = extension.reconfigure || (() => this.config);
  }

  getRicosExtension() {
    return this.ricosExtension;
  }

  toTiptapExtension(
    extensions: ExtensionAggregate,
    ricosProps: ExtensionProps,
    services: RicosServices
  ) {
    const ricosExtensions = extensions.getRicosExtensions();
    const config = this.reconfigure(
      this.config,
      ricosExtensions,
      ricosProps,
      this.settings,
      services
    );
    return Node.create(config);
  }
}
